import React, { cloneElement, isValidElement, ReactElement, useContext, useState } from 'react';
import { Permission } from '@/lib/api/interfaces/permissions.interface';
import { PlanKey } from '@/lib/api/interfaces/billing.interface';
import { usePermissions } from '@/lib/hooks/use-permissions';
import { Context } from '@/util-components/root/context';
import { UpgradePlanDialog } from '@/util-components/dialog/UpgradePlanDialog';
import { cn } from '@/lib/utils/cn';

interface PermissionFeatureWrapperProps {
  /**
   * The trigger element (e.g. PrimaryActionButton) to wrap
   */
  children: ReactElement;
  /**
   * The permission required to execute the action
   */
  requiredPermission: Permission | string;
  /**
   * The plan that includes this permission (displayed in the dialog)
   */
  requiredPlan: PlanKey;
}

/**
 * A wrapper component that intercepts clicks on a child element if the active user
 * lacks the required permission, and displays an UpgradePlanDialog instead.
 * 
 * Supports being used as a Radix/Shadcn trigger (handles onClick and ref pass-through).
 */
export const PermissionFeatureWrapper = React.forwardRef<HTMLElement, PermissionFeatureWrapperProps & React.HTMLAttributes<HTMLElement>>(({
  children,
  requiredPermission,
  requiredPlan,
  onClick,
  ...restProps
}, ref) => {
  const { can } = usePermissions();
  const { user } = useContext(Context);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  const hasPermission = can(requiredPermission);
  const currentPlan = user?.subscriptionType as string | undefined;

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    // If user lacks permission, always stop the event immediately
    if (!hasPermission) {
      e.preventDefault();
      e.stopPropagation();
      setShowUpgradeDialog(true);
      return;
    }

    // Call the onClick passed to this wrapper (e.g. from a DialogTrigger)
    if (onClick) {
      onClick(e);
    }

    // Only if user HAS permission, call the original onClick of the child
    if (isValidElement(children)) {
      const childProps = (children as any).props;
      if (childProps.onClick) {
        childProps.onClick(e);
      }
    }
  };

  const wrappedChild = isValidElement(children) ? (
    cloneElement(children as React.ReactElement<any>, {
      ...restProps,
      // @ts-ignore - ref might not be expected by all children but standard for triggers
      ref: ref || (children as any).ref,
      onClick: handleClick,
      className: cn(
        (children as any).props.className,
        restProps.className,
        !hasPermission && "opacity-60 grayscale cursor-not-allowed transition-all relative after:absolute after:inset-0 after:z-10"
      ),
      // Prevent children from becoming fully disabled in the DOM so they can still receive clicks for the upgrade dialog
      'aria-disabled': !hasPermission ? true : (children as any).props['aria-disabled']
    })
  ) : (
    <span 
      {...restProps}
      ref={ref as any}
      onClick={handleClick} 
      className={cn(restProps.className, !hasPermission && "opacity-60 grayscale cursor-not-allowed relative after:absolute after:inset-0 after:z-10")}
    >
      {children}
    </span>
  );

  return (
    <>
      {wrappedChild}
      <div onClick={(e) => e.stopPropagation()}>
        <UpgradePlanDialog
          open={showUpgradeDialog}
          onOpenChange={setShowUpgradeDialog}
          requiredPlan={requiredPlan}
          currentPlan={currentPlan}
        />
      </div>
    </>
  );
});

PermissionFeatureWrapper.displayName = "PermissionFeatureWrapper";
